import React, { useEffect, useState } from "react";
import axios from "axios";
import "./ViewPayments.css";
import { FaSearch } from "react-icons/fa";

const ViewPayments = () => {
  const [payments, setPayments] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPlan, setFilterPlan] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        const response = await axios.get(
          "https://back.varankal.com/api/payments/admin-report"
        );
        setPayments(response.data);
      } catch (error) {
        console.error("Error fetching payments:", error);
      }
    };
    fetchPayments();
  }, []);

  const openPopup = (payment) => {
    setSelectedTransaction(payment);
    setShowPopup(true);
  };

  const closePopup = () => {
    setSelectedTransaction(null);
    setShowPopup(false);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  // Safely get user's full name
  const getFullName = (user) => {
    if (!user) return "N/A";
    const basic = user.basicDetails || {};
    if (basic.name) return basic.name;
    return `${user.firstName || ""} ${user.lastName || ""}`.trim() || "N/A";
  };

  // Filter payments by search, status, plan, city
  const filteredPayments = payments.filter((payment) => {
    const name = getFullName(payment.userId).toLowerCase();
    const city = payment.userId?.city?.toLowerCase() || "";
    const plan = payment.planName?.toLowerCase() || "";
    const statusCheck = filterStatus
      ? filterStatus === "Completed"
        ? payment.isActive === true
        : payment.isActive === false
      : true;

    return (
      name.includes(searchTerm.toLowerCase()) &&
      statusCheck &&
      (filterPlan ? plan === filterPlan.toLowerCase() : true) &&
      (filterCity ? city === filterCity.toLowerCase() : true)
    );
  });

  // Sort filtered payments
  const sortedPayments = [...filteredPayments].sort((a, b) => {
    if (!sortColumn) return 0;
    let valueA, valueB;

    if (sortColumn === "amount") {
      valueA = a.amountPaid || 0;
      valueB = b.amountPaid || 0;
    } else if (sortColumn === "dateTime") {
      valueA = new Date(a.createdAt).getTime();
      valueB = new Date(b.createdAt).getTime();
    }

    if (valueA < valueB) return sortOrder === "asc" ? -1 : 1;
    if (valueA > valueB) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  // Pagination calculations
  const totalPages = Math.ceil(sortedPayments.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedPayments = sortedPayments.slice(indexOfFirstItem, indexOfLastItem);

  // Extract unique plans & cities for filter dropdowns
  const uniquePlans = [...new Set(payments.map((p) => p.planName).filter(Boolean))];
  const uniqueCities = [...new Set(payments.map((p) => p.userId?.city).filter(Boolean))];

  return (
    <div className="view-payments-container">
      <h1 className="view-payments-title">View Payments</h1>

      {/* Filters */}
      <div className="view-payments-filters">
        <div className="view-payments-search">
          <FaSearch />
          <input
            type="text"
            placeholder="Search by name"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset page on filter change
            }}
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="">All Status</option>
          <option value="Completed">Completed</option>
          <option value="Pending">Pending</option>
        </select>

        <select
          value={filterPlan}
          onChange={(e) => {
            setFilterPlan(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="">All Plans</option>
          {uniquePlans.map((plan) => (
            <option key={plan} value={plan}>
              {plan}
            </option>
          ))}
        </select>

        <select
          value={filterCity}
          onChange={(e) => {
            setFilterCity(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="">All Cities</option>
          {uniqueCities.map((city) => (
            <option key={city} value={city}>
              {city}
            </option>
          ))}
        </select>
      </div>

      {/* Payments Table */}
      <table className="view-payments-table">
        <thead>
          <tr>
            <th>User</th>
            <th>User Email</th>
            <th onClick={() => handleSort("amount")} style={{ cursor: "pointer" }}>
              Amount {sortColumn === "amount" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
            </th>
            <th>Status</th>
            <th onClick={() => handleSort("dateTime")} style={{ cursor: "pointer" }}>
              Date {sortColumn === "dateTime" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedPayments.length === 0 ? (
            <tr>
              <td colSpan="6" style={{ textAlign: "center" }}>
                No payments found.
              </td>
            </tr>
          ) : (
            paginatedPayments.map((payment) => (
              <tr key={payment._id}>
                <td>{getFullName(payment.userId)}</td>
                <td>{payment.userId?.email || "N/A"}</td>
                <td>${(payment.amountPaid || 0).toFixed(2)}</td>
                <td
                  className={`view-payments-status-${payment.isActive ? "completed" : "pending"
                    }`}
                >
                  {payment.isActive ? "Completed" : "Pending"}
                </td>
                <td>{new Date(payment.createdAt).toLocaleString()}</td>
                <td
                  className="view-details-btn"
                  onClick={() => openPopup(payment)}
                  style={{ cursor: "pointer" }}
                >
                  View Details
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Pagination Controls */}
      <div className="view-payments-pagination">
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
        >
          Prev
        </button>
        <span>
          Page {currentPage} of {totalPages || 1}
        </span>
        <button
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages || totalPages === 0}
        >
          Next
        </button>
      </div>

      {/* Payment Details Popup */}
      {showPopup && selectedTransaction && (
        <div className="view-payments-popup">
          <div className="popup-content">
            <button className="popup-close-button" onClick={closePopup}>
              &times;
            </button>
            <h2>Payment Details</h2>

            <div className="popup-row">
              <strong>User:</strong> {getFullName(selectedTransaction.userId)}
            </div>
            <div className="popup-row">
              <strong>Email:</strong> {selectedTransaction.userId?.email || "N/A"}
            </div>
            <div className="popup-row">
              <strong>Plan:</strong> {selectedTransaction.planName || "N/A"}
            </div>
            <div className="popup-row">
              <strong>Amount:</strong> $
              {(selectedTransaction.amountPaid || 0).toFixed(2)}
            </div>
            <div className="popup-row">
              <strong>Status:</strong>{" "}
              {selectedTransaction.isActive ? "Active" : "Inactive"}
            </div>
            <div className="popup-row">
              <strong>Payment Method:</strong>{" "}
              {selectedTransaction.paymentMethod || "N/A"}
            </div>
            <div className="popup-row">
              <strong>Start Date:</strong>{" "}
              {selectedTransaction.startDate
                ? new Date(selectedTransaction.startDate).toLocaleString()
                : "N/A"}
            </div>
            <div className="popup-row">
              <strong>Expiry Date:</strong>{" "}
              {selectedTransaction.expiryDate
                ? new Date(selectedTransaction.expiryDate).toLocaleString()
                : "N/A"}
            </div>
            <div className="popup-row">
              <strong>District:</strong>{" "}
              {selectedTransaction.location?.district || "N/A"}
            </div>
            <div className="popup-row">
              <strong>State:</strong> {selectedTransaction.location?.state || "N/A"}
            </div>
            <div className="popup-row">
              <strong>Account Status:</strong>{" "}
              {selectedTransaction.userId?.status || "N/A"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewPayments;
